/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import moment from 'moment';
import { action, observable } from 'mobx';
import {
	Model,
	IModelAttributes,
	attribute,
	entity,
	ReferencePath,
} from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from 'Util/AttributeUtils';
import { IAcl } from 'Models/Security/IAcl';
import {
	makeEnumFetchFunction,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import ApplicationUserAuditEntity from 'Models/Security/Acl/ApplicationUserAuditEntity';
import AgencyAdminAuditEntity from 'Models/Security/Acl/AgencyAdminAuditEntity';
import VisitorsAuditEntity from 'Models/Security/Acl/VisitorsAuditEntity';
import AgencyPlaylisterAuditEntity from 'Models/Security/Acl/AgencyPlaylisterAuditEntity';
import MustardManagementAuditEntity from 'Models/Security/Acl/MustardManagementAuditEntity';
import MustardAdminAuditEntity from 'Models/Security/Acl/MustardAdminAuditEntity';
import * as Enums from '../Enums';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IAuditEntityAttributes extends IModelAttributes {
	auditType: Enums.auditType;
	moodid: string;
	date: Date;
	trackid: string;

	zoneId?: string;
	zone?: Models.ZoneEntity | Models.IZoneEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('AuditEntity', 'Audit')
// % protected region % [Customise your entity metadata here] end
export default class AuditEntity extends Model
	implements IAuditEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new ApplicationUserAuditEntity(),
		new AgencyAdminAuditEntity(),
		new VisitorsAuditEntity(),
		new AgencyPlaylisterAuditEntity(),
		new MustardManagementAuditEntity(),
		new MustardAdminAuditEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Audit Type'] off begin
	@Validators.Required()
	@observable
	@attribute<AuditEntity, Enums.auditType>()
	@CRUD({
		name: 'Audit Type',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.auditTypeOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.auditTypeOptions),
		displayFunction: (attr: Enums.auditType) => Enums.auditTypeOptions[attr],
	})
	public auditType: Enums.auditType;
	// % protected region % [Modify props to the crud options here for attribute 'Audit Type'] end

	// % protected region % [Modify props to the crud options here for attribute 'MoodId'] off begin
	@observable
	@attribute<AuditEntity, string>()
	@CRUD({
		name: 'MoodId',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public moodid: string;
	// % protected region % [Modify props to the crud options here for attribute 'MoodId'] end

	// % protected region % [Modify props to the crud options here for attribute 'Date'] off begin
	@Validators.Required()
	@observable
	@attribute<AuditEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDateTimeNoTz(a) })
	@CRUD({
		name: 'Date',
		displayType: 'datetimepicker',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: e => AttrUtils.standardiseDate(e, { serialiseAs: 'dateTimeWithoutTz' }),
	})
	public date: Date;
	// % protected region % [Modify props to the crud options here for attribute 'Date'] end

	// % protected region % [Modify props to the crud options here for attribute 'TrackId'] off begin
	@observable
	@attribute<AuditEntity, string>()
	@CRUD({
		name: 'TrackId',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public trackid: string;
	// % protected region % [Modify props to the crud options here for attribute 'TrackId'] end

	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Zone'] off begin
		name: 'Zone',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.ZoneEntity,
		// % protected region % [Modify props to the crud options here for reference 'Zone'] end
	})
	public zoneId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public zone: Models.ZoneEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAuditEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAuditEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.auditType !== undefined) {
				this.auditType = attributes.auditType;
			}
			if (attributes.moodid !== undefined) {
				this.moodid = attributes.moodid;
			}
			if (attributes.date !== undefined) {
				if (attributes.date === null) {
					this.date = attributes.date;
				} else {
					this.date = moment(attributes.date).toDate();
				}
			}
			if (attributes.trackid !== undefined) {
				this.trackid = attributes.trackid;
			}
			if (attributes.zoneId !== undefined) {
				this.zoneId = attributes.zoneId;
			}
			if (attributes.zone !== undefined) {
				if (attributes.zone === null) {
					this.zone = attributes.zone;
				} else if (attributes.zone instanceof Models.ZoneEntity) {
					this.zone = attributes.zone;
					this.zoneId = attributes.zone.id;
				} else {
					this.zone = new Models.ZoneEntity(attributes.zone);
					this.zoneId = this.zone.id;
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		zone {
			${Models.ZoneEntity.getAttributes().join('\n')}
			${Models.ZoneEntity.getFiles().map(f => f.name).join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
						],
					},
				],
			},
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] off begin
		return this.id;
		// % protected region % [Customise the display name for this entity] end
	}

	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AuditEntity.prototype, 'created');
CRUD(modifiedAttr)(AuditEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
